import React, { useEffect, useState } from 'react';
import styles from './style.module.css';
import { StoreAIBot } from '../../components/StoreAIBot';
import Typography from '../../components/Typography/Typography';
import { StoreCart, StoreImgName, StoreSearchIconDark } from '../../assets/svgs';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { useGetTemplateByIdQuery, useUpdateStoreMutation } from '../../redux/services/storesApi';
import AnimatedButton from '../../components/AnimatedButton';
import { resetStoreState } from '../../redux/slices/createProductSlice';
import { resetStore } from '../../redux/slices/createStoreSlice';

interface BoutiqueInfoProps {
  backgroundImage?: string;
  overlayImage?: string;
}

export const PreviewStoreProducts: React.FC<BoutiqueInfoProps> = ({
  backgroundImage,
  overlayImage
}) => {
  const [storeVisibility, setStoreVisibility] = useState('public');
  const [errorMessage, setErrorMessage] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storeDetails = useSelector((state: RootState) => state.CreateStore);
  const products = useSelector((state: RootState) => state.CreateProduct);

  const { data: templateData } = useGetTemplateByIdQuery(
    storeDetails.storeTemplate || '',
    { skip: !storeDetails.storeTemplate }
  );

  const [updateStore] = useUpdateStoreMutation();

  const currentBackgroundImage = templateData?.image_preview_url || backgroundImage;
  const currentOverlayImage = templateData?.model_url || overlayImage;

  useEffect(() => {
    console.log('=== PreviewStore Debug ===');
    console.log('Store Details:', storeDetails);
    console.log('Store ID:', storeDetails.storeId);
    console.log('Products:', products);
    console.log('Template Data:', templateData);
  }, [storeDetails, products, templateData]);

  const handlePublish = async () => {
    setIsPublishing(true);
    setErrorMessage("");

    try {
      if (!storeDetails.storeId) {
        setErrorMessage('Store ID not found. Please go back and complete store creation.');
        setIsPublishing(false);
        return;
      }

      const updateStoreData = {
        is_public: storeVisibility === 'public'
      };
      await updateStore({
        storeId: storeDetails.storeId,
        payload: updateStoreData
      }).unwrap();

      dispatch(resetStoreState());
      dispatch(resetStore());

      console.log('Store published successfully with visibility:', storeVisibility);

      navigate("/congratulations-message");

    } catch (err: any) {
      console.error('Publish error:', err);

      let errorMsg = 'Failed to publish store. Please try again.';

      if (err?.data?.detail) {
        if (Array.isArray(err.data.detail)) {
          errorMsg = err.data.detail.map((e: any) => e.msg || e.message).join(', ');
        } else if (typeof err.data.detail === 'string') {
          errorMsg = err.data.detail;
        }
      } else if (err?.data?.message) {
        errorMsg = err.data.message;
      }

      setErrorMessage(errorMsg);

      if (err?.status === 500 || err?.status === 404) {
        navigate("/store-error");
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleEdit = () => {
    if (storeDetails.storeTemplate) {
      navigate(`/add-info/${storeDetails.storeTemplate}`);
    } else {
      navigate('/add-info');
    }
  };

  const reduxProducts = useSelector((state: RootState) => state.CreateProduct);

  const getProductPosition = (position: string, index: number) => {
    const positionMap: { [key: string]: { x: number; y: number } } = {
      'A1': { x: 50, y: 80 },
      'A2': { x: 50, y: 180 },
      'B1': { x: 180, y: 80 },
      'B2': { x: 180, y: 180 },
      'C1': { x: 310, y: 80 },
      'C2': { x: 310, y: 180 },
    };
    return positionMap[position] || { x: 50 + (index * 80), y: 120 };
  };

  return (
    <div className={styles.boutiqueContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Typography
              variant="heading5"
              fontFamily="satoshi"
              weight="bold"
              className={styles.title}
            >
              Preview For Publishing
            </Typography>
            <Typography
              variant="bodyRegular"
              fontFamily="satoshi"
              weight="regular"
              className={styles.headerSubtext}
            >
              Review your store before publishing. You can edit details or change visibility settings.
            </Typography>
          </div>
          <AnimatedButton
            variant="secondary"
            onClick={handleEdit}
            className={styles.editBtn}
          >
            <Typography
              variant="buttonText"
              fontFamily="satoshi"
              weight="regular"
              className={styles.editBtnText}
            >
              Edit
            </Typography>
          </AnimatedButton>
        </div>

        <div className={styles.storePreviewSection}>
          <div className={styles.storeContainer}>
            <div className={styles.topNav}>
              <FlatButtons className={styles.storeName}>
                {storeDetails.logoFile ? (
                  <img
                    src={storeDetails.logoFile}
                    alt="Store Logo"
                    className={styles.storeLogo}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <img
                    src={StoreImgName}
                    alt="Store Icon"
                    className={styles.icon}
                  />
                )}
                <Typography
                  variant="buttonText"
                  fontFamily="satoshi"
                  weight="regular"
                  className={styles.storeNameText}
                >
                  {storeDetails?.name || 'Store Name'}
                </Typography>
              </FlatButtons>
              <FlatButtons className={styles.navIcons}>
                <img
                  src={StoreSearchIconDark}
                  alt="Search"
                  className={styles.icon}
                />
                <img
                  src={StoreCart}
                  alt="Cart"
                  className={styles.icon}
                />
              </FlatButtons>
            </div>

            <div className={styles.storeLayout}>
              {currentBackgroundImage && (
                <img
                  src={currentBackgroundImage}
                  alt="Store Layout"
                  className={styles.backgroundImage}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (backgroundImage) {
                      target.src = backgroundImage;
                    }
                  }}
                />
              )}

              {currentOverlayImage && (
                <div className={styles.overlay}>
                  <img
                    src={currentOverlayImage}
                    alt="Store overlay"
                    className={styles.overlayImage}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (overlayImage) {
                        target.src = overlayImage;
                      }
                    }}
                  />
                </div>
              )}

              {reduxProducts && reduxProducts.length > 0 && reduxProducts.map((product, index) => {
                const position = getProductPosition(product.position_in_store, index);

                return (
                  <div 
                  key={`store-product-${index}`} 
                  className={styles.storeProduct} 
                  style={{ 
                    left: `${position.x}px`, 
                    top: `${position.y}px` 
                    }}
                    >
                      {product.image_file && (
                    <img 
                    src={product.image_file ?? ''} 
                    alt={product.name} 
                    className={styles.storeProductImage}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                    />
                  )}
                    <Typography
                      variant="bodySmall"
                      fontFamily="satoshi"
                      weight="regular"
                      className={styles.storeProductName}
                    >
                      {product.name}
                      </Typography>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className={styles.privacySection}>
          <div className={styles.privacyOption}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="storeVisibility"
                value="public"
                checked={storeVisibility === 'public'}
                onChange={(e) => setStoreVisibility(e.target.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioCustom}></span>
              <div className={styles.radioContent}>
                <Typography
                  variant="bodyRegular"
                  fontFamily="satoshi"
                  weight="bold"
                  className={styles.privacyTitle}
                >
                  Make Store Public
                </Typography>
                <Typography
                  variant="bodyRegular"
                  fontFamily="satoshi"
                  weight="regular"
                  className={styles.privacyDescription}
                >
                  Store will be featured in the OpenX marketplace for the public to view. You can also share your link to attract more people to your store. This setting can be changed in store settings.
                </Typography>
              </div>
            </label>
          </div>

          <div className={styles.privacyOption}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="storeVisibility"
                value="private"
                checked={storeVisibility === 'private'}
                onChange={(e) => setStoreVisibility(e.target.value)}
                className={styles.radioInput}
              />
              <span className={styles.radioCustom}></span>
              <div className={styles.radioContent}>
                <Typography
                  variant="bodyRegular"
                  fontFamily="satoshi"
                  weight="bold"
                  className={styles.privacyTitle}
                >
                  Make Store Private
                </Typography>
                <Typography
                  variant="bodyRegular"
                  fontFamily="satoshi"
                  weight="regular"
                  className={styles.privacyDescription}
                >
                  Make store private. Only those you share your store link with can view the store, and it will not be featured in the OpenX marketplace. This setting can be changed in store settings.
                </Typography>
              </div>
            </label>
          </div>

          <div className={styles.continueEditingSection}>
            <FlatButtons
              className={styles.notDoneBtn}
              onClick={handleEdit}
            >
              <Typography
                variant="bodyRegular"
                fontFamily="satoshi"
                weight="bold"
                className={styles.continueEditing}
              >
                Not done yet? Continue editing store
              </Typography>
            </FlatButtons>
          </div>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            <Typography
              variant="bodyRegular"
              fontFamily="satoshi"
              weight="regular"
              className={styles.errorText}
            >
              {errorMessage}
            </Typography>
          </div>
        )}

        <div className={styles.publishContainer}>
          <AnimatedButton
            variant="primary"
            onClick={handlePublish}
            className={styles.publishBtn}
            disabled={isPublishing}
          >
            <Typography
              variant="buttonText"
              fontFamily="satoshi"
              weight="regular"
              className={styles.publishBtnText}
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </Typography>
          </AnimatedButton>
        </div>
      </div>

      <StoreAIBot />
    </div>
  );
}

